import React, { useCallback } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { format } from "date-fns";
import { Box, Text, VStack, StackDivider, Icon } from "@chakra-ui/react";
import ItemRow from "./ItemRow";
import {
  STATUS_ICON_MAP,
  localizeTransactionStatus,
} from "./utils";
import { getLocaleTzAbbrFromDate } from "utils";

const SPACING = 2;

/**
 * MetaSummary - Presentational component for displaying meta info like dates, status, etc
 * @param {object} props
 * @param {TxnVariant} props.transaction
 * @returns JSX.Element
 */
export default function MetaSummary({ transaction }) {
  const { formatMessage, formatDate } = useIntl();
  const {
    transaction_type,
    transaction_id,
    transaction_status,
    transaction_data,
    account_id,
    transaction_date,
  } = transaction;
  const statusIcon = STATUS_ICON_MAP[transaction_status];

  const getDateRows = useCallback(
    (dateStr, prefix) => {
      const eventType =
        prefix ||
        formatMessage({
          defaultMessage: "Transaction",
          description: "Transaction like purchase transaction. Field label.",
        });
      const rows = [];
      if (dateStr) {
        const date = new Date(dateStr);
        const ctz = getLocaleTzAbbrFromDate(date);
        rows.push({
          title: formatMessage(
            {
              defaultMessage: "{eventType} date",
              description: "Example: Transaction date. Field label.",
            },
            {
              eventType,
            },
          ),
          value: formatDate(date, {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
        });
        rows.push({
          title: formatMessage(
            {
              defaultMessage: "{eventType} time",
              description: "Example: Transaction time. Field label.",
            },
            {
              eventType,
            },
          ),
          value: `${format(date, "h:mmaaa")}${ctz ? ` (${ctz})` : ""}`,
        });
      }
      return rows;
    },
    [formatDate, formatMessage],
  );

  let rows = [
    {
      title: formatMessage({
        defaultMessage: "Status",
        description: "Field label",
      }),
      value: localizeTransactionStatus(transaction, formatMessage),
      capitalizeValue: true,
      leftValueIcon: statusIcon ? (
        <Icon
          mr={2}
          color={transaction_status === "failed" ? "errorText" : "integrator.500"}
          as={statusIcon}
        />
      ) : null,
    },
    {
      title: formatMessage({
        defaultMessage: "Transaction ID",
        description: "Field label",
      }),
      value: transaction_id,
    },
    {
      title: formatMessage({
        defaultMessage: "Account ID",
        description: "Field label",
      }),
      value: account_id,
    },
  ];

  const dateRows = getDateRows(transaction_date); // not all variants will include these date rows

  let created, updated, createdRows, updatedRows;

  switch (transaction_type) {
    case "balance_transfer": // @type {TxnBalanceTransferType}
    case "user_purchase": // @type {TxnBuyerData}
    case "user_purchase_escrow": // @type {TxnBuyerData}
    case "user_purchase_recipient": // @type {TxnSellerData}
    case "token_purchase": // @type {TokenPurchaseData}
    case "token_convert": // @type {TokenPurchaseData}
    case "refund": // @type {RefundBuyerData}
      const { reference_type, reference_id, original_reference_type, original_reference_id } =
        transaction_data;
      if (reference_type || original_reference_type) {
        rows.push({
          title: formatMessage({
            defaultMessage: "Reference type",
            description: "Field label",
          }),
          value: reference_type || original_reference_type,
        });
      }
      if (reference_id || original_reference_id) {
        rows.push({
          title: formatMessage({
            defaultMessage: "Reference ID",
            description: "Field label",
          }),
          value: reference_id || original_reference_id,
        });
      }
      rows = [...rows, ...dateRows];
      break;
    case "payout":
      const payout = transaction_data.payout || {}; // @type {TxnPurchaseUserData}
      created = payout.created;
      updated = payout.updated;
      createdRows = getDateRows(
        created,
        formatMessage({
          defaultMessage: "Requested",
          description: "Field label",
        }),
      );
      updatedRows = [];
      if (transaction_status !== "pending") {
        updatedRows = getDateRows(
          updated,
          formatMessage({
            defaultMessage: "Processed",
            description: "Field label",
          }),
        );
      }
      rows = [...rows, ...createdRows, ...updatedRows];
      break;
    case "deposit": // @type {DepositData}
      const deposit = transaction_data.deposit || {};
      created = deposit.created;
      updated = deposit.updated;
      createdRows = getDateRows(
        created,
        formatMessage({
          defaultMessage: "Requested",
          description: "Field label",
        }),
      );
      updatedRows = [];
      if (transaction_status !== "pending") {
        updatedRows = getDateRows(
          updated,
          formatMessage({
            defaultMessage: "Processed",
            description: "Field label",
          }),
        );
      }
      rows = [...rows, ...createdRows, ...updatedRows];
      break;
    default:
      /**
       * If this is an unsupported transaction_type,
       * we return null.  It's up to whatever
       * is wrapping this component to show appropriate
       * messaging about non-supported transaction types.
       */
      return null;
  }

  return (
    <Box pt={4} mb={2}>
      <Text fontSize="sm" fontWeight="semibold" pb={4}>
        <FormattedMessage defaultMessage="Invoice details" description="Table title" />
      </Text>
      <VStack align="left" spacing={SPACING} divider={<StackDivider />}>
        {rows.map((row, i) => (
          <ItemRow key={i} {...row} />
        ))}
      </VStack>
    </Box>
  );
}
